/**
 * PreviewContext
 *
 * Provides preview mode state management for teachers viewing course content.
 * - isPreviewMode: Signals components to disable progress tracking
 * - showAnswers: Controls whether quiz answers are displayed
 * - currentProgram/week/module: Navigation state for preview
 * - Caches API responses to avoid reloading when navigating
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";

// Import types from previewService to avoid duplication
import type {
  PreviewModuleContent,
  PreviewQuestion,
  PreviewModuleSummary,
  PreviewWeekSummary as PreviewWeek,
  PreviewProgramSummary as PreviewProgram,
} from "../services/previewService";

// Import additional types for caching
import type {
  PreviewProgramDetail,
  PreviewWeekDetail,
} from "../services/previewService";

// Re-export for convenience
export type {
  PreviewModuleContent,
  PreviewQuestion,
  PreviewModuleSummary,
  PreviewWeek,
  PreviewProgram,
};

// Cache types - use any[] for sections to avoid import issues
export interface CachedModuleData {
  quizData: PreviewModuleContent | null;
  learningConfig: any | null;
  loadedSections: any[]; // LoadedSection[] - using any to avoid circular imports
  timestamp: number;
}

// ============================================================================
// Context Type
// ============================================================================

interface PreviewContextType {
  // Core preview mode flags
  isPreviewMode: boolean;
  showAnswers: boolean;
  setShowAnswers: (show: boolean) => void;
  toggleShowAnswers: () => void;

  // Current navigation state
  currentProgram: string | null;
  currentWeek: number | null;
  currentModuleType: string | null;

  // Navigation setters
  setCurrentProgram: (programId: string | null) => void;
  setCurrentWeek: (weekNumber: number | null) => void;
  setCurrentModuleType: (moduleType: string | null) => void;

  // Convenience method to set all navigation at once
  navigateToModule: (
    programId: string,
    weekNumber: number,
    moduleType: string
  ) => void;

  // Reset state
  resetPreviewState: () => void;

  // Active content tab (learning vs quiz)
  activeTab: "learning" | "quiz";
  setActiveTab: (tab: "learning" | "quiz") => void;

  // ============================================================================
  // Cache API - Programs
  // ============================================================================

  getCachedProgram: (programId: string) => PreviewProgramDetail | null;
  setCachedProgram: (programId: string, data: PreviewProgramDetail) => void;

  // ============================================================================
  // Cache API - Weeks
  // ============================================================================

  getCachedWeek: (
    programId: string,
    weekNumber: number
  ) => PreviewWeekDetail | null;
  setCachedWeek: (
    programId: string,
    weekNumber: number,
    data: PreviewWeekDetail
  ) => void;

  // ============================================================================
  // Cache API - Modules
  // ============================================================================

  // Get cached module data
  getCachedModule: (moduleKey: string) => CachedModuleData | null;

  // Cache quiz data from backend
  setCachedQuizData: (moduleKey: string, data: PreviewModuleContent) => void;

  // Cache learning content (config + loaded sections)
  setCachedLearningContent: (
    moduleKey: string,
    config: any,
    sections: any[] // LoadedSection[]
  ) => void;

  // Check if module is cached
  isModuleCached: (moduleKey: string) => boolean;

  // Clear cache (optional - for refresh)
  clearCache: () => void;

  // Generate cache key from params
  getModuleKey: (program: string, week: number, moduleType: string) => string;
}

// ============================================================================
// Context & Hook
// ============================================================================

const PreviewContext = createContext<PreviewContextType | undefined>(undefined);

export const usePreview = () => {
  const context = useContext(PreviewContext);
  if (!context) {
    throw new Error("usePreview must be used within a PreviewProvider");
  }
  return context;
};

// ============================================================================
// Provider Component
// ============================================================================

interface PreviewProviderProps {
  children: ReactNode;
}

// Cache expiry time (30 minutes)
const CACHE_TTL = 30 * 60 * 1000;

export const PreviewProvider: React.FC<PreviewProviderProps> = ({
  children,
}) => {
  // Core preview mode state
  const [showAnswers, setShowAnswers] = useState<boolean>(false);

  // Navigation state
  const [currentProgram, setCurrentProgram] = useState<string | null>(null);
  const [currentWeek, setCurrentWeek] = useState<number | null>(null);
  const [currentModuleType, setCurrentModuleType] = useState<string | null>(
    null
  );

  // Content tab state
  const [activeTab, setActiveTab] = useState<"learning" | "quiz">("learning");

  // ============================================================================
  // Program Cache
  // ============================================================================

  const [programCache, setProgramCache] = useState<
    Map<string, { data: PreviewProgramDetail; timestamp: number }>
  >(new Map());

  // Keep a ref to the cache for stable getter functions
  const programCacheRef = React.useRef(programCache);
  programCacheRef.current = programCache;

  // Get cached program data (stable - uses ref)
  const getCachedProgram = useCallback(
    (programId: string): PreviewProgramDetail | null => {
      const key = programId.toLowerCase();
      const cached = programCacheRef.current.get(key);
      if (!cached) return null;

      // Check if cache is expired
      if (Date.now() - cached.timestamp > CACHE_TTL) {
        setProgramCache((prev) => {
          const newCache = new Map(prev);
          newCache.delete(key);
          return newCache;
        });
        return null;
      }

      return cached.data;
    },
    [] // No dependencies - uses ref
  );

  // Set cached program data
  const setCachedProgram = useCallback(
    (programId: string, data: PreviewProgramDetail) => {
      const key = programId.toLowerCase();
      setProgramCache((prev) => {
        const newCache = new Map(prev);
        newCache.set(key, { data, timestamp: Date.now() });
        return newCache;
      });
    },
    []
  );

  // ============================================================================
  // Week Cache
  // ============================================================================

  const [weekCache, setWeekCache] = useState<
    Map<string, { data: PreviewWeekDetail; timestamp: number }>
  >(new Map());

  // Keep a ref to the cache for stable getter functions
  const weekCacheRef = React.useRef(weekCache);
  weekCacheRef.current = weekCache;

  // Generate week cache key
  const getWeekKey = (programId: string, weekNumber: number): string => {
    return `${programId.toLowerCase()}-week${weekNumber}`;
  };

  // Get cached week data (stable - uses ref)
  const getCachedWeek = useCallback(
    (programId: string, weekNumber: number): PreviewWeekDetail | null => {
      const key = getWeekKey(programId, weekNumber);
      const cached = weekCacheRef.current.get(key);
      if (!cached) return null;

      // Check if cache is expired
      if (Date.now() - cached.timestamp > CACHE_TTL) {
        setWeekCache((prev) => {
          const newCache = new Map(prev);
          newCache.delete(key);
          return newCache;
        });
        return null;
      }

      return cached.data;
    },
    [] // No dependencies - uses ref
  );

  // Set cached week data
  const setCachedWeek = useCallback(
    (programId: string, weekNumber: number, data: PreviewWeekDetail) => {
      const key = getWeekKey(programId, weekNumber);
      setWeekCache((prev) => {
        const newCache = new Map(prev);
        newCache.set(key, { data, timestamp: Date.now() });
        return newCache;
      });
    },
    []
  );

  // ============================================================================
  // Module Content Cache
  // ============================================================================

  const [moduleCache, setModuleCache] = useState<Map<string, CachedModuleData>>(
    new Map()
  );

  // Keep a ref to the cache for stable getter functions
  const moduleCacheRef = React.useRef(moduleCache);
  moduleCacheRef.current = moduleCache;

  // Generate cache key
  const getModuleKey = useCallback(
    (program: string, week: number, moduleType: string): string => {
      return `${program.toLowerCase()}-week${week}-${moduleType.toLowerCase()}`;
    },
    []
  );

  // Get cached module data (stable - uses ref)
  const getCachedModule = useCallback(
    (moduleKey: string): CachedModuleData | null => {
      const cached = moduleCacheRef.current.get(moduleKey);
      if (!cached) return null;

      // Check if cache is expired
      if (Date.now() - cached.timestamp > CACHE_TTL) {
        // Remove expired cache
        setModuleCache((prev) => {
          const newCache = new Map(prev);
          newCache.delete(moduleKey);
          return newCache;
        });
        return null;
      }

      return cached;
    },
    [] // No dependencies - uses ref
  );

  // Check if module is cached
  const isModuleCached = useCallback(
    (moduleKey: string): boolean => {
      return getCachedModule(moduleKey) !== null;
    },
    [getCachedModule]
  );

  // Cache quiz data
  const setCachedQuizData = useCallback(
    (moduleKey: string, data: PreviewModuleContent) => {
      setModuleCache((prev) => {
        const newCache = new Map(prev);
        const existing = newCache.get(moduleKey);
        newCache.set(moduleKey, {
          quizData: data,
          learningConfig: existing?.learningConfig || null,
          loadedSections: existing?.loadedSections || [],
          timestamp: Date.now(),
        });
        return newCache;
      });
    },
    []
  );

  // Cache learning content
  const setCachedLearningContent = useCallback(
    (moduleKey: string, config: any, sections: any[]) => {
      setModuleCache((prev) => {
        const newCache = new Map(prev);
        const existing = newCache.get(moduleKey);
        newCache.set(moduleKey, {
          quizData: existing?.quizData || null,
          learningConfig: config,
          loadedSections: sections,
          timestamp: Date.now(),
        });
        return newCache;
      });
    },
    []
  );

  // Clear all cache
  const clearCache = useCallback(() => {
    setProgramCache(new Map());
    setWeekCache(new Map());
    setModuleCache(new Map());
  }, []);

  // ============================================================================
  // Navigation Methods
  // ============================================================================

  // Toggle answer visibility
  const toggleShowAnswers = useCallback(() => {
    setShowAnswers((prev) => !prev);
  }, []);

  // Navigate to a specific module (sets all navigation state at once)
  const navigateToModule = useCallback(
    (programId: string, weekNumber: number, moduleType: string) => {
      setCurrentProgram(programId);
      setCurrentWeek(weekNumber);
      setCurrentModuleType(moduleType);
      // Reset to learning tab when navigating to new module
      setActiveTab("learning");
      // Don't reset showAnswers - preserve user preference
    },
    []
  );

  // Reset all preview state
  const resetPreviewState = useCallback(() => {
    setCurrentProgram(null);
    setCurrentWeek(null);
    setCurrentModuleType(null);
    setShowAnswers(false);
    setActiveTab("learning");
    // Don't clear cache on reset - keep it for performance
  }, []);

  // Context value
  const value: PreviewContextType = {
    // Always true within this provider - components can check this
    isPreviewMode: true,
    showAnswers,
    setShowAnswers,
    toggleShowAnswers,

    currentProgram,
    currentWeek,
    currentModuleType,

    setCurrentProgram,
    setCurrentWeek,
    setCurrentModuleType,

    navigateToModule,
    resetPreviewState,

    activeTab,
    setActiveTab,

    // Cache API - Programs
    getCachedProgram,
    setCachedProgram,

    // Cache API - Weeks
    getCachedWeek,
    setCachedWeek,

    // Cache API - Modules
    getCachedModule,
    setCachedQuizData,
    setCachedLearningContent,
    isModuleCached,
    clearCache,
    getModuleKey,
  };

  return (
    <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>
  );
};

// ============================================================================
// Utility Hook: Check if in Preview Mode (safe to use outside provider)
// ============================================================================

/**
 * Safe hook that returns false if not within PreviewProvider
 * Use this in shared components that may or may not be in preview mode
 */
export const useIsPreviewMode = (): boolean => {
  const context = useContext(PreviewContext);
  return context?.isPreviewMode ?? false;
};

/**
 * Safe hook that returns showAnswers state, or false if not in preview mode
 */
export const useShowAnswers = (): boolean => {
  const context = useContext(PreviewContext);
  return context?.showAnswers ?? false;
};

export default PreviewContext;
